import type {
  ExpenditureDto,
  ExpenditureDetailDto,
  ExpenditureListResponse,
  ExpenditureListParams,
  CreateExpenditureRequest,
  VoidRowRequest,
  TriageItem,
  RowVersion,
  SortInboxExpenditureDto,
  UpdateExpenditureRequest,
} from '@signalvote/shared';
import { updateExpenditureRequest } from '@signalvote/shared';
import { apiJson, apiResponse, staleVersion } from './apiFetch';
import { refusal, throwStagedRowError } from './useFinanceContributions';
import {
  editInboxRow,
  markConfirmed,
  markPendingReview,
  markVoided,
  markReported,
  markUnreported,
  patchExpenditureCategory,
  patchExpenditureSavedDates,
} from './sortInboxOptimistic';
import { defineWrite } from '@/lib/data';
import { defineStatusFlip } from './optimisticStatusFlip';

/**
 * Kind predicate selecting an Expenditure row in the sort-inbox cache — the row
 * identity the optimistic patches key on.
 */
const expenditureRow = (expenditureId: string) => (item: TriageItem): boolean =>
  item.kind === 'Expenditure' && item.expenditure.id === expenditureId;

// ============================================================================
// Query Hooks
// ============================================================================

// ============================================================================
// Mutation Hooks
// ============================================================================

export const useCreateExpenditure = defineWrite({
  send: (data: CreateExpenditureRequest) =>
    apiJson<ExpenditureDto>('/api/finance/expenditures', {
      method: 'POST',
      json: data,
      error: refusal('Failed to create expenditure'),
    }),
});

// ============================================================================
// BSW-014: Staged-page detail panel hooks
// ============================================================================

/** The fields an expenditure edit re-sends unchanged, read off the row the edit lands on. */
export type EditableExpenditure = Pick<
  SortInboxExpenditureDto,
  'id' | 'concurrencyVersion' | 'category' | 'purpose' | 'authorizedDate' | 'incurredDate'
>;

/**
 * The fields an expenditure edit changes. Category and purpose it leaves out are re-sent as the
 * row has them when the edit runs, and so is the other event date of an `updateEventDates` edit.
 * Payee, amount, date and the debt and loan links it leaves out are not sent, which the server
 * reads as unchanged.
 */
export type ExpenditureEdit = Partial<Omit<UpdateExpenditureRequest, 'concurrencyVersion'>>;

export const useUpdateExpenditure = defineWrite({
  row: 'Expenditure',
  send: async (id, concurrencyVersion: RowVersion, edit: ExpenditureEdit, row: EditableExpenditure) => {
    const eventDates = edit.updateEventDates
      ? { authorizedDate: row.authorizedDate, incurredDate: row.incurredDate }
      : {};
    const response = await apiResponse(`/api/finance/expenditures/${id}`, {
      method: 'PATCH',
      json: updateExpenditureRequest({
        category: row.category,
        purpose: row.purpose,
        ...eventDates,
        ...edit,
        concurrencyVersion,
      }),
    });
    if (!response.ok) {
      await throwStagedRowError(response, 'Failed to update expenditure');
    }
    return response.json() as Promise<ExpenditureDto>;
  },
  // Root cause #3: the close-ledger category `<select>` reads its value straight off the
  // sort-inbox cache with no draft state, so a save would show the OLD category until the
  // write-set refetch lands. Patch ONLY the edited row's category on click; an edit that leaves
  // the category alone patches nothing. The saved dates land in the row from the response, so the
  // date cells show the save before the refetch.
  patch: ({ row, category }) =>
    category === undefined ? [] : [editInboxRow(expenditureRow(row.id), patchExpenditureCategory(category))],
  land: (saved, { row }) => [editInboxRow(expenditureRow(row.id), patchExpenditureSavedDates(saved))],
});

// ULL-005 (design doc D2/D9): the expenditure ✓-uncheck on the All-Time
// Ledger. POSTs /api/finance/expenditures/{id}/unconfirm (ULL-002), the
// inverse of the born-Pending confirm flip — Confirmed → Pending, the row
// stays an Expenditure (no kind-morph). Mirror of `useUnconfirmContribution`.
//
// Distinct error envelope from the BSW-014 PATCH/void endpoints (which
// throw `StagedRowUpdateError`): the (un)confirm endpoints return their own
// `{ code }` payload from `ExpenditureUnconfirmErrorCodes`
// (`ExpenditureNotFound` → 404, `ConcurrencyConflict` → 409, everything else
// → 400). Surfacing it as a typed error class keeps the ledger's
// refusal-to-copy mapping (`IsReported`, concurrency) structurally distinct
// from the staged-row "modified elsewhere" message. (RFR-006 dropped the
// `RefundLinked` refusal — no Expenditure is ever a refund's half.)
//
// Named for the confirm/unconfirm FAMILY (not `…UnconfirmError`): when the
// born-Pending `useConfirmExpenditure` consumer lands it shares the same
// `ExpenditureConfirm/UnconfirmErrorCodes` envelope and reuses this class —
// mirroring how `ContributionConfirmError` backs both contribution flips.
export class ExpenditureConfirmError extends Error {
  code: string;
  status: number;
  constructor(
    code: string,
    status: number,
    message: string,
    /** The row's version on a 409 stale write, read by the write door. */
    readonly currentVersion: number | null = null,
  ) {
    super(message);
    this.name = 'ExpenditureConfirmError';
    this.code = code;
    this.status = status;
  }
}

// ULL-005: the expenditure ✓-uncheck on the All-Time Ledger — Confirmed →
// Pending, the inverse of useConfirmExpenditure.
export const useUnconfirmExpenditure = defineStatusFlip({
  endpoint: {
    entity: 'Expenditure',
    send: (id, concurrencyVersion) =>
      apiJson<ExpenditureDto>(`/api/finance/expenditures/${id}/unconfirm`, {
        method: 'POST',
        json: { concurrencyVersion },
        error: (body, response) =>
          new ExpenditureConfirmError(body.code ?? '', response.status, body.error ?? 'Failed to uncheck expenditure', staleVersion(body)),
      }),
  },
  patch: markPendingReview,
});

// ULL-003: the born-Pending → Confirmed flip for an expenditure on the
// All-Time Ledger. POSTs /api/finance/expenditures/{id}/confirm (ULL-002) —
// the path bank-classified (`BankTransactionClassifyAsExpenditureService`)
// and receipt-captured (`ReceiptCaptureService`) expenditures take to become
// filable. Inverse of `useUnconfirmExpenditure`; shares the same
// `ExpenditureConfirmError` envelope (the consumer the hook-family comment
// above anticipated). The backend refuses Reported + refund-FK-linked rows
// (D9) with `{ code }` payloads the ledger maps to treasurer copy.
// ULL-003: the born-Pending → Confirmed flip for an expenditure on the All-Time
// Ledger — the path bank-classified + receipt-captured expenditures take to
// become filable. Inverse of useUnconfirmExpenditure; status-only, aggregates
// untouched.
export const useConfirmExpenditure = defineStatusFlip({
  endpoint: {
    entity: 'Expenditure',
    send: (id, concurrencyVersion) =>
      apiJson<ExpenditureDto>(`/api/finance/expenditures/${id}/confirm`, {
        method: 'POST',
        json: { concurrencyVersion },
        error: (body, response) =>
          new ExpenditureConfirmError(body.code ?? '', response.status, body.error ?? 'Failed to confirm expenditure', staleVersion(body)),
      }),
  },
  patch: markConfirmed,
});

// AL EDI delta-export (design §6): the manual "mark as reported" carve-out for
// an expenditure the treasurer entered directly in the AL FCPA portal. Flips
// Confirmed → Reported so our rules see it without us re-exporting it (which
// would duplicate it on the append-only portal — ADR-020). Mirror of
// `useMarkContributionReported`; reuses the `ExpenditureConfirmError` envelope
// (wire codes from `ExpenditureMarkReportedErrorCodes`). The optional `note` is
// dropped by JSON.stringify when undefined.
// AL EDI delta-export: the manual "mark as reported" carve-out for an expenditure
// the treasurer entered directly in the AL FCPA portal (Confirmed → Reported).
// Mirror of useMarkContributionReported; the optional `note` is dropped by
// JSON.stringify when undefined.
export const useMarkExpenditureReported = defineStatusFlip({
  endpoint: {
    entity: 'Expenditure',
    send: (id, concurrencyVersion, { note }: { note?: string }) =>
      apiJson<ExpenditureDto>(`/api/finance/expenditures/${id}/mark-reported`, {
        method: 'POST',
        json: { concurrencyVersion, note },
        error: (body, response) =>
          new ExpenditureConfirmError(body.code ?? '', response.status, body.error ?? 'Failed to mark this expenditure as reported', staleVersion(body)),
      }),
  },
  patch: markReported,
});

// AL EDI delta-export (design §6): reverse a manual mark
// (Reported → Confirmed) for an expenditure, returning it to the export pool.
// The audited, provenance-gated carve-out of ADR-007.
// AL EDI delta-export: reverse a manual mark (Reported → Confirmed) for an
// expenditure, returning it to the export pool. Status-only flip.
export const useUnmarkExpenditureReported = defineStatusFlip({
  endpoint: {
    entity: 'Expenditure',
    send: (id, concurrencyVersion) =>
      apiJson<ExpenditureDto>(`/api/finance/expenditures/${id}/unmark-reported`, {
        method: 'POST',
        json: { concurrencyVersion },
        error: (body, response) =>
          new ExpenditureConfirmError(body.code ?? '', response.status, body.error ?? 'Failed to undo the mark-reported', staleVersion(body)),
      }),
  },
  patch: markUnreported,
});

// Stamp the row "Not reported" (Done/Voided) on click; the write set refreshes the
// aggregates it shifts (contacts, obligations) and (ATL-006) drops the expenditure
// from the all-time inbox queue.
export const useVoidExpenditure = defineStatusFlip({
  endpoint: {
    entity: 'Expenditure',
    send: async (id, concurrencyVersion) => {
      const response = await apiResponse(`/api/finance/expenditures/${id}/void`, {
        method: 'POST',
        json: { concurrencyVersion } satisfies VoidRowRequest,
      });
      if (!response.ok) {
        await throwStagedRowError(response, 'Failed to void expenditure');
      }
      return response.json() as Promise<ExpenditureDto>;
    },
  },
  patch: markVoided,
});

// ADR-016 un-void — restores a Voided expenditure to Pending (re-review). The
// inverse of `useVoidExpenditure`; mirror of `useUnvoidContribution`. The
// restore button lives in the cancellation chooser dialog — this hook is its
// data path.
export const useUnvoidExpenditure = defineStatusFlip({
  endpoint: {
    entity: 'Expenditure',
    send: async (id, concurrencyVersion) => {
      const response = await apiResponse(`/api/finance/expenditures/${id}/unvoid`, {
        method: 'POST',
        json: { concurrencyVersion } satisfies VoidRowRequest,
      });
      if (!response.ok) {
        await throwStagedRowError(response, 'Failed to restore expenditure');
      }
      return response.json() as Promise<ExpenditureDto>;
    },
  },
  patch: markPendingReview,
});

// ADR-016 single-row Delete — permanently removes a non-Reported expenditure
// (a mis-entry that never happened). Mirror of `useDeleteContribution`: 204 (no
// payload), concurrency token as a query param, the `StagedRowUpdateError`
// envelope carrying `DeleteErrorCodes`.
export const useDeleteExpenditure = defineWrite({
  row: 'Expenditure',
  send: async (id, concurrencyVersion: RowVersion): Promise<void> => {
    const response = await apiResponse(
      `/api/finance/expenditures/${id}?concurrencyVersion=${concurrencyVersion}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      await throwStagedRowError(response, 'Failed to delete expenditure');
    }
    // 204 No Content — no payload.
  },
});
